import { useEffect, useState } from 'react';
import { ArrowPathIcon, DocumentArrowDownIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { SelectDropdown } from '../SelectDropdown';
import { OverlayGrayBackground } from '../OverlayGrayBackground';

export const UserDrawer = ({ 
    optionsByRoles,
    isOpen, 
    isLoading,
    onClose, 
    onSuccessSubmit, 
    user 
}) => {
    const [fullName, setFullName] = useState(null);
    const [email, setEmail] = useState(null);
    const [slackId, setSlackId] = useState(null);
    const [password, setPassword] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);

    useEffect(() => {
        if (
            selectedRole !== user?.roleId || 
            fullName !== user?.name || 
            email !== user?.email ||
            password !== user?.password ||
            slackId !== user?.slackId
        ) {
            setShowUnsavedChangesWarning(true);
        }
    }, [selectedRole, fullName, email, password, slackId])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (!isOpen) {
            setSelectedRole(null);
            setPassword(null);
            setEmail(null);
            setFullName(null);
            setShowUnsavedChangesWarning(false);
            setSlackId(null);
        }
        // Disable scroll when drawer is open
        if (isOpen) {
            setSelectedRole(user?.roleId);
            setPassword(user?.password);
            setSlackId(user?.slackId);
            setEmail(user?.email);
            setFullName(user?.name);
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const handleOnClose = () => {
        onClose?.();
        setSelectedRole(null);
        setPassword(null);
        setEmail(null);
        setFullName(null);
        setShowUnsavedChangesWarning(false);
    }

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col`}
            >
                <div className='p-6 border-b border-border'>
                    <div className='flex flex-col mb-3 gap-4'>
                        <div className='flex items-center justify-between'>
                            <h2 className="text-lg font-semibold">
                                Edit User Permissions
                            </h2>
                            <button 
                                onClick={onClose}
                                className='inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 h-8 rounded-md gap-1.5 px-3'
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        {showUnsavedChangesWarning && (
                            <div className='flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4'>
                                <ExclamationTriangleIcon className='h-5 w-5 text-amber-800'/>
                                <span className='text-amber-800'>You have unsaved changes</span>
                            </div>
                        )}
                    </div>
                    <div className='flex items-center gap-3'>
                        <span className='relative flex size-8 shrink-0 overflow-hidden rounded-full'>
                            <span className='bg-gray-50 flex size-full items-center justify-center rounded-full'>
                                {(user?.name || "")
                                    .split(" ")
                                    .map(word => word[0] || "")
                                    .join("")
                                    .toUpperCase()}
                            </span>
                        </span>
                        <div>
                            <div className='font-medium'>
                                {user?.name || ""}
                            </div>
                            <div className='text-sm text-muted-foreground'>
                                {user?.email || ""}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='border-b border-border flex flex-col flex-1 overflow-y-auto overflow-x-hidden relative'>
                    {isLoading && <OverlayGrayBackground />}
                    <div className='p-6 gap-4 flex flex-col'>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <label className='flex items-center gap-2 text-sm leading-none font-medium select-none'>
                                    Full Name
                                </label>
                                <input
                                    id="fullName"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="emailAddress">
                                    Email Address
                                </label>
                                <input
                                    id="emailAddress"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="password">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    value={password}
                                    placeholder="********************"
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                            <div className='space-y-2'>
                                <label className='flex items-center gap-2 text-sm leading-none font-medium select-none'>
                                    Role
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={selectedRole}
                                        options={optionsByRoles || []}
                                        value={selectedRole}
                                        onChange={setSelectedRole}
                                        selectClassname='bg-white px-3 py-2'
                                    />
                                </div>
                            </div>
                        </div> 
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="password">
                                    Slack ID
                                </label>
                                <input
                                    id="slackId"
                                    value={slackId}
                                    placeholder="Slack ID"
                                    onChange={(e) => setSlackId(e.target.value)}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                        </div> 
                        {/* <p className='text-xs text-muted-foreground'>
                            Applies a default set of tabs. You can customize below.
                        </p>          */}
                    </div>
                </div>
                <div className='p-6 border-t border-border'>
                    <div className='flex gap-2'>
                        <button 
                            onClick={() => {
                                if (
                                    selectedRole !== user?.roleId || 
                                    fullName !== user?.name || 
                                    email !== user?.email ||
                                    password !== user?.password ||
                                    slackId !== user?.slackId
                                ) {
                                    onSuccessSubmit?.({
                                        id: user?.id,
                                        newRoleId: selectedRole,
                                        fullName,
                                        email,
                                        password,
                                        slackId
                                    });
                                } else {
                                    handleOnClose();
                                }
                            }}
                            className='inline-flex hover:bg-gray-800 items-center text-white justify-center gap-2 whitespace-nowrap rounded-md text-xs font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none bg-primary flex-1 h-8'
                        >
                            <DocumentArrowDownIcon className='h-5 w-5 text-white' />
                            <span>
                                Save
                            </span>
                        </button>
                        <button
                            onClick={() => {
                                handleOnClose();
                            }}
                            className='group inline-flex hover:bg-gray-200 items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-background shadow-xs h-8 px-3 py-1.5'
                        >
                            <ArrowPathIcon className='h-5 w-5 group-hover:text-white' />
                            <span className='group-hover:text-white'>
                                Discard
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};